import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { useSettledDataQuery } from '@ps/web-shared-generated-graphql/sports';
import type { SettledDataQueryVariables } from '@ps/web-shared-generated-graphql/sports';
import React, { useContext, useReducer } from 'react';

import { EventTypeId } from '../types/eventType';

import type { ComponentEventTypeEvents, ScoreboardDataReducerAction } from './types';
import { mapEventTypesEventIdsMap, scoreboardsDataReducer } from './utils';

export interface SettledDataState {
  componentEventTypeEvents: ComponentEventTypeEvents;
  footballPlayerIds: string[];
  variables: Omit<SettledDataQueryVariables, 'footballEventIds' | 'footballPlayerIds'>;
}

export type SettledDataStateVariables = SettledDataState['variables'];

export interface SettledDataContextValue {
  dispatch: React.Dispatch<ScoreboardDataReducerAction>;
  isLoading?: boolean;
  isError?: boolean;
  variables: SettledDataStateVariables;
}

interface Props extends React.PropsWithChildren {
  initialState?: SettledDataState;
  variables?: SettledDataStateVariables;
}

const SettledDataContext = React.createContext<SettledDataContextValue | null>(null);

export const SettledDataProvider: React.FC<Props> = ({
  initialState = {
    componentEventTypeEvents: {},
    footballPlayerIds: [],
    variables: {
      includeTeamStats: false,
      includePlayers: false,
      includeIncidents: false
    }
  },
  children
}) => {
  const [settledDataState, dispatch] = useReducer(scoreboardsDataReducer, initialState);
  const { footballPlayerIds, variables, componentEventTypeEvents } = settledDataState;
  const isLiveFootballDataEnabled = useFeature(FeatureFlag.ISPEnableLiveFootballData);
  const eventTypesEventIdsMap = mapEventTypesEventIdsMap(componentEventTypeEvents);

  const footballEventIds = isLiveFootballDataEnabled
    ? eventTypesEventIdsMap[EventTypeId.SOCCER] ?? []
    : [];

  const hasFootballEventIds = Boolean(footballEventIds.length);
  const hasPlayerIds = Boolean(footballPlayerIds.length);
  const skip = !hasPlayerIds || !hasFootballEventIds;

  const settledDataQuery = useSettledDataQuery({
    variables: {
      footballEventIds: footballEventIds.map(String),
      footballPlayerIds,
      ...variables
    },
    skip,
    errorPolicy: 'all',
    fetchPolicy: 'network-only'
  });

  const { loading, error } = settledDataQuery;

  return (
    <SettledDataContext.Provider
      value={{
        dispatch,
        isLoading: loading,
        isError: Boolean(error),
        variables
      }}
    >
      {children}
    </SettledDataContext.Provider>
  );
};

export const useSettledData = (): SettledDataContextValue => {
  const context = useContext(SettledDataContext);

  if (context === null) {
    throw new Error('You must wrap "useSettledData" in a "SettledDataProvider"');
  }

  return context;
};
